import { Link } from 'react-router-dom';
import { useState } from 'react';
import FloatingSocialIcons from '../../components/base/FloatingSocialIcons';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ProjectCarousel from '../../components/base/ProjectCarousel';
import VideoBackground from '../../components/base/VideoBackground';

const HomePage = () => {
  const [flippedCards, setFlippedCards] = useState<number[]>([]);
  const [flippedPainPoints, setFlippedPainPoints] = useState<number[]>([]);

  const services = [
    {
      icon: 'ri-google-fill',
      title: 'Google My Business Management',
      description: 'Optimize your Google My Business profile to increase local visibility, manage reviews, and attract more customers in your area.',
      benefits: [
        'Increase local search visibility by up to 300% in your area',
        'Generate 5x more customer reviews and build trust',
        'Appear in Google Maps and local pack results',
        'Manage all customer reviews from one dashboard'
      ]
    },
    {
      icon: 'ri-code-s-slash-fill',
      title: 'Web Design',
      description: 'Strengthen your online visibility with a custom responsive website that customers and search engines love.',
      benefits: [
        'Convert 40% more visitors into paying customers',
        'Rank higher on Google with mobile-optimized design',
        'Professional design that builds instant credibility',
        'Fast loading speeds that keep visitors engaged'
      ]
    },
    {
      icon: 'ri-map-pin-fill',
      title: 'Business Listings',
      description: 'Digital Experience Studio listings services put you in control of the facts about your business across 70+ digital platforms globally.',
      benefits: [
        'Appear in 70+ online directories automatically',
        'Increase online visibility by 250% across platforms',
        'Consistent business information everywhere',
        'Boost local SEO rankings significantly'
      ]
    },
    {
      icon: 'ri-share-fill',
      title: 'Social Media Marketing & Management',
      description: 'Increase your social media fan base, locate real-time leads, and publish relevant content across all of your platforms.',
      benefits: [
        'Grow your follower base by 150% in 6 months',
        'Generate 3x more leads through social engagement',
        'Save 15+ hours per week on content creation',
        'Increase brand awareness and customer loyalty'
      ]
    },
    {
      icon: 'ri-bar-chart-fill',
      title: 'Reputation Management',
      description: '97% of consumers find local business info on the web, you need a way to see what your customers are saying and a platform to join the conversation.',
      benefits: [
        'Increase positive reviews by 200% within 90 days',
        'Protect your brand reputation 24/7 with monitoring',
        'Respond to reviews quickly and professionally',
        'Turn negative feedback into positive outcomes'
      ]
    },
    {
      icon: 'ri-search-fill',
      title: 'Search Engine Optimization',
      description: '80% of consumers search online before making a buying decision. <a href="/" class="text-cyan-400 hover:text-cyan-300 font-semibold transition-colors duration-300">Digital Experience Studio</a> helps your website rank higher, get noticed, and drive more sales.',
      benefits: [
        'Achieve first page Google rankings in 3-6 months',
        'Increase organic website traffic by 400%',
        'Dominate your local market search results',
        'Generate qualified leads that convert to sales'
      ]
    },
    {
      icon: 'ri-file-text-fill',
      title: 'Content Services',
      description: 'Great content is the backbone of every successful business. <a href="/" class="text-cyan-400 hover:text-cyan-300 font-semibold transition-colors duration-300">Digital Experience Studio</a> recognizes the value of high-quality content and can illustrate it through first-rate online content writing services.',
      benefits: [
        'Engage customers with content that converts 60% better',
        'Establish authority in your industry with expert content',
        'Improve SEO rankings with optimized content',
        'Build trust and credibility with your audience'
      ]
    },
    {
      icon: 'ri-advertisement-fill',
      title: 'PPC Ad Campaign Management',
      description: 'Drive immediate results with targeted pay-per-click advertising campaigns across Google, Facebook, and other platforms that convert clicks into customers.',
      benefits: [
        'See immediate traffic increases within 24-48 hours',
        'Achieve 300% return on ad spend with optimized campaigns',
        'Target your ideal customers with precision',
        'Lower cost per acquisition by 40% with expert management'
      ]
    },
    {
      icon: 'ri-funnel-fill',
      title: 'Sales Funnel Development',
      description: 'Sales funnels magnify your message, reach a wider targeted audience, and fill your pipeline with qualified leads more quickly than any traditional internet marketing method.',
      benefits: [
        'Convert 50% more leads into paying customers',
        'Automate your sales process and save 20 hours per week',
        'Nurture leads automatically with email sequences',
        'Track every step of the customer journey'
      ]
    },
    {
      icon: 'ri-map-2-fill',
      title: 'Local SEO',
      description: 'Dominate local search results and attract nearby customers with targeted local SEO strategies that put your business on the map.',
      benefits: [
        'Appear in top 3 local search results consistently',
        'Increase foot traffic and local calls by 180%',
        'Outrank competitors in your service area',
        'Get found by customers searching "near me"'
      ]
    },
    {
      icon: 'ri-rocket-fill',
      title: 'Digital Marketing',
      description: 'Comprehensive digital marketing strategies that combine multiple channels to maximize your online presence and drive measurable business growth.',
      benefits: [
        'Grow your business revenue by 250% in 12 months',
        'Reach your ideal customers across all digital platforms',
        'Integrated strategy that works together seamlessly',
        'Data-driven decisions that maximize ROI'
      ]
    },
    {
      icon: 'ri-camera-3d-fill',
      title: 'Virtual Tours',
      description: 'Elevate your brand with <a href="/" class="text-cyan-400 hover:text-cyan-300 font-semibold transition-colors duration-300">Digital Experience Studio</a> Virtual Tours, where stunning 360° photography meets immersive virtual experiences.',
      benefits: [
        'Increase customer engagement by 300% with immersive experiences',
        'Stand out from competitors with cutting-edge virtual showcases',
        'Let customers explore your space 24/7 from anywhere',
        'Boost booking confidence and reduce no-shows'
      ]
    }
  ];

  const handleCardClick = (index: number) => {
    setFlippedCards((prev) => {
      if (prev.includes(index)) {
        return prev.filter((cardIndex) => cardIndex !== index);
      }
      return [...prev, index];
    });
  };

  const painPoints = [
    {
      icon: 'ri-time-fill',
      title: 'No Time for Social Media',
      description: 'Running a business leaves little time for creating consistent, engaging social media content that actually converts.',
      benefits: [
        'We create and schedule all your social media content for you',
        'Save 15+ hours per week on content creation and posting',
        'Consistent posting schedule keeps your brand top-of-mind',
        'Professional content that engages and converts followers'
      ]
    },
    {
      icon: 'ri-question-fill',
      title: 'Don\'t Know What Content Works',
      description: 'Posting random content without strategy leads to poor engagement and wasted marketing budget.',
      benefits: [
        'Data-driven content strategy based on what works in your industry',
        'A/B testing to optimize every post for maximum engagement',
        'Analytics and insights to track what resonates with your audience',
        'Proven content formulas that drive clicks, leads, and sales'
      ]
    },
    {
      icon: 'ri-line-chart-fill',
      title: 'Low Website Conversion',
      description: 'Your website gets traffic but visitors leave without taking action - no leads, no sales, no growth.',
      benefits: [
        'Conversion-optimized website design that turns visitors into customers',
        'Clear calls-to-action that guide visitors to take the next step',
        'Fast loading speeds and mobile optimization for better user experience',
        'Strategic placement of forms and contact options to capture leads'
      ]
    },
    {
      icon: 'ri-user-unfollow-fill',
      title: 'Losing Customers to Competitors',
      description: 'Competitors with better online presence are stealing your potential customers every day.',
      benefits: [
        'Dominate local search results and appear above competitors',
        'Professional branding that makes you stand out in your market',
        'Active social media presence that builds trust and credibility',
        'Strategic marketing that positions you as the industry leader'
      ]
    },
    {
      icon: 'ri-money-dollar-circle-fill',
      title: 'Wasting Marketing Budget',
      description: 'Spending money on ads and marketing that don\'t generate real leads or measurable ROI.',
      benefits: [
        'Targeted campaigns that reach your ideal customers only',
        'Detailed ROI tracking so you know exactly what\'s working',
        'Optimized ad spend to maximize results and minimize waste',
        'Transparent reporting showing every dollar\'s impact on your business'
      ]
    },
    {
      icon: 'ri-smartphone-fill',
      title: 'Outdated Online Presence',
      description: 'Your website and social media look unprofessional, making potential customers question your credibility.',
      benefits: [
        'Modern, professional website design that builds instant trust',
        'Cohesive branding across all platforms for consistent image',
        'Mobile-responsive design that looks perfect on every device',
        'Regular updates to keep your online presence fresh and current'
      ]
    }
  ];

  const handlePainPointClick = (index: number) => {
    setFlippedPainPoints((prev) => {
      if (prev.includes(index)) {
        return prev.filter((cardIndex) => cardIndex !== index);
      }
      return [...prev, index];
    });
  };

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      <VideoBackground />
      <FloatingSocialIcons />
      <Header />
      
      <main className="relative z-10">
        {/* Hero Section */}
        <section 
          className="relative min-h-screen flex items-center justify-center bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.7)), url('https://static.readdy.ai/image/cfeaaf915a66e3490d7a0ef017ccbecc/0e226a3206759fb18d3a3214fe75d73f.jpeg')`
          }}
        >
          <div className="container mx-auto px-6 text-center text-white">
            <div className="bg-black/60 backdrop-blur-sm rounded-3xl p-12 shadow-2xl border border-cyan-400/30 shadow-cyan-400/20">
              <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Digital Experience Studio
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-gray-200 max-w-3xl mx-auto leading-relaxed">
                Transform your small business with professional website design and digital marketing that generates leads, builds brand awareness, and drives real results. Digital Experience Studio creates websites that convert and marketing that works! Industry specific & we appreciate marketing budgets of all sizes and will work with you and we will grow together. 
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/consultation"
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-8 py-4 rounded-full text-lg font-bold hover:from-cyan-400 hover:to-blue-500 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-cyan-400/30 whitespace-nowrap cursor-pointer"
                >
                  Get Free Consultation
                </Link>
                <Link
                  to="/projects"
                  className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-full text-lg font-bold hover:bg-white/20 transition-all duration-300 border border-cyan-400/50 whitespace-nowrap cursor-pointer"
                >
                  View Our Work
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Mini Intro Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-6">
            <div className="bg-gray-900/90 backdrop-blur-sm rounded-3xl shadow-2xl p-12 border border-cyan-400/30 shadow-cyan-400/10">
              <div className="text-center mb-12">
                <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent mb-6">
                  Why Choose <a href="/" className="text-cyan-400 hover:text-cyan-300 font-semibold transition-colors duration-300">Digital Experience Studio</a> for Your Small Business?
                </h2>
                <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
                  <a href="/" className="text-cyan-400 hover:text-cyan-300 font-semibold transition-colors duration-300">Digital Experience Studio</a> specializes in helping small businesses succeed online with professional website design and digital marketing strategies that actually work. From stunning websites that convert visitors into customers to social media campaigns that generate real leads, <a href="/" className="text-cyan-400 hover:text-cyan-300 font-semibold transition-colors duration-300">Digital Experience Studio</a> is your one-stop solution for digital growth.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center p-6 bg-gradient-to-br from-gray-800 to-black rounded-2xl border border-cyan-400/20">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-full mx-auto mb-4 shadow-lg shadow-cyan-400/30">
                    <i className="ri-trophy-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-cyan-400 mb-3">Proven Results</h3>
                  <p className="text-gray-300"><a href="/" className="text-cyan-400 hover:text-cyan-300 font-semibold transition-colors duration-300">Digital Experience Studio</a> has helped hundreds of small businesses increase their online presence, generate more leads, and grow their revenue through strategic digital marketing.</p>
                </div>

                <div className="text-center p-6 bg-gradient-to-br from-gray-800 to-black rounded-2xl border border-green-400/20">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-full mx-auto mb-4 shadow-lg shadow-green-400/30">
                    <i className="ri-wallet-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-green-400 mb-3">Affordable Solutions</h3>
                  <p className="text-gray-300">Professional website design and digital marketing services designed specifically for small business budgets without compromising on quality.</p>
                </div>

                <div className="text-center p-6 bg-gradient-to-br from-gray-800 to-black rounded-2xl border border-purple-400/20">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-purple-500 to-violet-600 text-white rounded-full mx-auto mb-4 shadow-lg shadow-purple-400/30">
                    <i className="ri-customer-service-2-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-purple-400 mb-3">100% Customer Satisfaction Guarantee</h3>
                  <p className="text-gray-300">We stand behind our work with complete customer satisfaction guarantee and ongoing support to ensure your continued success.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services List Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent mb-6">
                <a href="/" className="text-cyan-400 hover:text-cyan-300 font-semibold transition-colors duration-300">Digital Experience Studio</a> Complete Digital Marketing Services
              </h2>
              <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
                From professional website design to comprehensive digital marketing strategies, <a href="/" className="text-cyan-400 hover:text-cyan-300 font-semibold transition-colors duration-300">Digital Experience Studio</a> offers everything your small business needs to succeed online and outshine the competition.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => {
                const isFlipped = flippedCards.includes(index);
                
                return (
                  <div
                    key={index}
                    className="cursor-pointer perspective-1000 h-96"
                    onClick={() => handleCardClick(index)}
                  >
                    <div 
                      className={`relative w-full h-full transition-transform duration-700 transform-style-3d ${
                        isFlipped ? 'rotate-y-180' : ''
                      }`}
                      style={{
                        transformStyle: 'preserve-3d',
                        transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
                      }}
                    >
                      {/* Front Side */}
                      <div 
                        className="absolute w-full h-full backface-hidden bg-gray-900/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group border border-cyan-400/30 shadow-cyan-400/10"
                        style={{ backfaceVisibility: 'hidden' }}
                      >
                        <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-cyan-400/30">
                          <i className={`${service.icon} text-3xl text-white`}></i>
                        </div>
                        <h3 className="text-xl font-bold mb-4 text-cyan-400">{service.title}</h3>
                        <p 
                          className="text-gray-300 mb-6 text-sm leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: service.description }}
                        ></p>
                        <div className="absolute bottom-8 left-8 right-8">
                          <div className="text-center bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg shadow-cyan-400/30">
                            Click to see benefits
                          </div>
                        </div>
                      </div>

                      {/* Back Side - Benefits */}
                      <div 
                        className="absolute w-full h-full backface-hidden bg-gradient-to-br from-cyan-600 via-blue-500 to-purple-600 rounded-3xl p-8 shadow-xl border border-cyan-400/50 shadow-cyan-400/20"
                        style={{ 
                          backfaceVisibility: 'hidden',
                          transform: 'rotateY(180deg)'
                        }}
                      >
                        <div className="h-full flex flex-col text-white">
                          <div className="mb-6">
                            <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl mb-4">
                              <i className={`${service.icon} text-2xl`}></i>
                            </div>
                            <h3 className="text-xl font-bold mb-2 leading-tight">{service.title}</h3>
                            <h4 className="font-bold text-lg text-yellow-300">Key Benefits:</h4>
                          </div>
                          
                          <div className="flex-1 space-y-4 overflow-y-auto">
                            {service.benefits.map((benefit, idx) => (
                              <div key={idx} className="flex items-start">
                                <div className="w-6 h-6 flex items-center justify-center bg-white/20 rounded-full mr-3 mt-0.5 flex-shrink-0">
                                  <i className="ri-check-line text-yellow-300 text-sm font-bold"></i>
                                </div>
                                <span className="text-sm leading-relaxed">{benefit}</span>
                              </div>
                            ))}
                          </div>

                          <div className="text-center mt-6 pt-4 border-t border-white/20">
                            <p className="text-sm text-white/90 font-semibold">Click to flip back</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="text-center mt-12">
              <p className="text-gray-300 text-lg mb-6">
                <i className="ri-hand-coin-line mr-2"></i>
                Click any service card to see how it benefits your small business
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/consultation"
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-8 py-4 rounded-full text-lg font-bold hover:from-cyan-400 hover:to-blue-500 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-cyan-400/30 whitespace-nowrap cursor-pointer inline-block"
                >
                  Get Free Consultation
                </Link>
                <Link
                  to="/quote"
                  className="bg-gradient-to-r from-gray-700 to-gray-800 text-white px-8 py-4 rounded-full text-lg font-bold hover:from-gray-800 hover:to-gray-900 transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer inline-block border border-cyan-400/50"
                >
                  Request Free Quote
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Recent Projects Carousel */}
        <section className="py-20 relative">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent mb-6">
                Recent Website Design Projects, Social Media Content & PPC Ads
              </h2>
              <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
                Explore <a href="/" className="text-cyan-400 hover:text-cyan-300 font-semibold transition-colors duration-300">Digital Experience Studio</a> latest work showcasing professional website designs, engaging social media content, and high-converting PPC advertising campaigns that have helped small businesses achieve remarkable growth.
              </p>
            </div>

            <ProjectCarousel />

            <div className="text-center mt-12">
              <Link
                to="/projects"
                className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-8 py-4 rounded-full text-lg font-bold hover:from-cyan-400 hover:to-blue-500 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-cyan-400/30 whitespace-nowrap cursor-pointer inline-block"
              >
                View All Projects
              </Link>
            </div>
          </div>
        </section>

        {/* Pain Points Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-6">
            <div className="bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-700 rounded-3xl shadow-2xl p-12 text-white border border-blue-400/30 shadow-blue-400/20">
              <div className="text-center mb-12">
                <h2 className="text-4xl md:text-5xl font-bold mb-6">
                  Are These Small Business Challenges Holding You Back?
                </h2>
                <p className="text-xl text-blue-100 max-w-4xl mx-auto leading-relaxed">
                  Don't let these common problems prevent your business from reaching its full potential. <a href="/" className="text-blue-200 hover:text-blue-100 font-semibold transition-colors duration-300">Digital Experience Studio</a> understands the struggles small businesses face with social media marketing and website conversion - and <a href="/" className="text-blue-200 hover:text-blue-100 font-semibold transition-colors duration-300">Digital Experience Studio</a> has the solutions.
                </p>
                <p className="text-lg text-blue-200 mt-4 font-semibold">
                  <i className="ri-hand-coin-line mr-2"></i>
                  Click any challenge card to see how we solve it
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {painPoints.map((point, index) => {
                  const isFlipped = flippedPainPoints.includes(index);
                  
                  return (
                    <div
                      key={index}
                      className="cursor-pointer perspective-1000 h-80"
                      onClick={() => handlePainPointClick(index)}
                    >
                      <div 
                        className={`relative w-full h-full transition-transform duration-700 transform-style-3d ${
                          isFlipped ? 'rotate-y-180' : ''
                        }`}
                        style={{
                          transformStyle: 'preserve-3d',
                          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
                        }}
                      >
                        {/* Front Side - Problem */}
                        <div 
                          className="absolute w-full h-full backface-hidden text-center p-6 bg-black/20 backdrop-blur-sm rounded-2xl border border-blue-400/30"
                          style={{ backfaceVisibility: 'hidden' }}
                        >
                          <div className="w-16 h-16 flex items-center justify-center bg-blue-500/30 rounded-full mx-auto mb-4">
                            <i className={`${point.icon} text-2xl text-blue-300`}></i>
                          </div>
                          <h3 className="text-xl font-bold mb-3 text-blue-300">{point.title}</h3>
                          <p className="text-blue-100 mb-6">{point.description}</p>
                          <div className="absolute bottom-6 left-6 right-6">
                            <div className="text-center bg-white/20 text-white px-4 py-2 rounded-full text-sm font-semibold">
                              Click to see our solution
                            </div>
                          </div>
                        </div>

                        {/* Back Side - Solution Benefits */}
                        <div 
                          className="absolute w-full h-full backface-hidden bg-gradient-to-br from-green-500 via-emerald-500 to-teal-600 rounded-2xl p-6 shadow-xl border border-green-400/50"
                          style={{ 
                            backfaceVisibility: 'hidden',
                            transform: 'rotateY(180deg)'
                          }}
                        >
                          <div className="h-full flex flex-col text-white">
                            <div className="mb-4">
                              <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl mb-3">
                                <i className={`${point.icon} text-xl`}></i>
                              </div>
                              <h3 className="text-lg font-bold mb-2 leading-tight">{point.title}</h3>
                              <h4 className="font-bold text-base text-yellow-300">Our Solution:</h4>
                            </div>
                            
                            <div className="flex-1 space-y-3 overflow-y-auto">
                              {point.benefits.map((benefit, idx) => (
                                <div key={idx} className="flex items-start">
                                  <div className="w-5 h-5 flex items-center justify-center bg-white/20 rounded-full mr-2 mt-0.5 flex-shrink-0">
                                    <i className="ri-check-line text-yellow-300 text-xs font-bold"></i>
                                  </div>
                                  <span className="text-sm leading-relaxed">{benefit}</span>
                                </div>
                              ))}
                            </div>

                            <div className="text-center mt-4 pt-3 border-t border-white/20">
                              <p className="text-xs text-white/90 font-semibold">Click to flip back</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="text-center">
                <h3 className="text-2xl font-bold mb-4">Ready to Solve These Problems?</h3>
                <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
                  Let's discuss how <a href="/" className="text-blue-200 hover:text-blue-100 font-semibold transition-colors duration-300">Digital Experience Studio</a> proven strategies can transform your online presence and turn these challenges into opportunities for growth.
                </p>
                <Link
                  to="/consultation"
                  className="bg-white text-blue-600 px-8 py-4 rounded-full text-lg font-bold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer inline-block"
                >
                  Get Your Free Solution Consultation
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-6">
            <div 
              className="relative bg-cover bg-center rounded-3xl shadow-2xl overflow-hidden border border-cyan-400/30 shadow-cyan-400/20"
              style={{
                backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.9)), url('https://public.readdy.ai/ai/img_res/9517650453c2ce9f1b91ae5f25d27766.jpg')`
              }}
            >
              <div className="p-12 text-center text-white">
                <h2 className="text-4xl md:text-5xl font-bold mb-6 text-cyan-400">
                  Ready to Transform Your Business Online?
                </h2>
                <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8 leading-relaxed">
                  Don't let your competitors get ahead. Start your digital transformation today with a free, no-obligation consultation. Let's discuss how <a href="/" className="text-cyan-400 hover:text-cyan-300 font-semibold transition-colors duration-300">Digital Experience Studio</a> can help your business grow with professional website design and strategic digital marketing!
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/consultation"
                    className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-8 py-4 rounded-full text-lg font-bold hover:from-cyan-400 hover:to-blue-500 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-cyan-400/30 whitespace-nowrap cursor-pointer"
                  >
                    Book Free Consultation
                  </Link>
                  <Link
                    to="/quote"
                    className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-full text-lg font-bold hover:bg-white/20 transition-all duration-300 border border-cyan-400/50 whitespace-nowrap cursor-pointer"
                  >
                    Request Free Quote
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;

